import { clone, forEach } from 'lodash';
import { createLogger } from 'lbs-pi-handy/src/general-purpose/logger';
import { triggerUScopeEvent } from '../../_uScope';
import us from '../../defines';
import { makeObservable } from '../../../../shared/stores/core/make-observable';

const logger = createLogger('UScope:lib:UserPreferences');

// original values were not set. False should be equiv. TODO: Replace when ready.
const NOT_SET = false;

const defaults = {
  [us.clsDIVZOOMSLIDER]: {value: true, dbId: null},
  [us.clsDIVZOOMCTL]: {value: true, dbId: null},
  [us.clsDIVTHUMBNAIL]: {value: true, dbId: null},
  [us.CVSHEATMAP]: {value: NOT_SET, dbId: null},
  [us.clsDIVTHUMBHOVER]: {value: true, dbId: null},
  [us.clsDIVGUAGES]: {value: NOT_SET, dbId: null},
  [us.clsDIVSCALEBAR]: {value: true, dbId: null},
  [us.clsROTATIONDIV]: {value: true, dbId: null},
  [us.clsDIVMAG]: {value: false, dbId: null},
  [us.clsDIVLABEL]: {value: NOT_SET, dbId: null},
  // Report Settings
  reportRegionWidth: {value: us.eReportSpec.defWidthPx, dbId: null},
  reportRegionHeight: {value: us.eReportSpec.defHeightPx, dbId: null},
  reportRegionType: {value: us.eReportSpec.defReportType, dbId: null},
  // UI settings
  labelRotation: {value: 0, dbId: null},
  imageQuality: {value: 80 /* NOT_SET */, dbId: null}
};

/**
 * Creates a fake platform access provider. Useful if not connected to a dataserver.
 * @returns {{data: []}|{createConfigValue(), getUserConfig(): {data: []}, updateConfigValue()}}
 */
function makeLocalApi () {
  const key = 'us:user-pref-local';
  const data = restore() || {};

  function save () {
    localStorage.setItem(key, JSON.stringify(data));
  }
  function restore () {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : null;
  }

  return {
    getUserConfig () {
      return { data };
    },
    updateConfigValue ({id, name, value}) {
      data[name] = {id, value};
      save();
    },
    createConfigValue ({name, value}) {
      const id = Object.keys(data).length;
      data[name] = {id, value};
      save();
      return id;
    },
    isLocalApi: true
  };
}

export default function UserPreferences (uScope, platformAccess) {
  const self = this;
  const userId = uScope.getUserId();
  let api = platformAccess?.dataServer?.user?.userConfigProvider || makeLocalApi();
  let userPrefTable;

  self.events = uScope.events.$new();

  /**
   * Loads user pref's from DataServer
   * @returns {Promise<void>}
   */
  this.load = async () => {
    let forceLocalApi = false;
    if (us.DISABLEUSERPREFS2DB) {
      logger.log('Syncing user pref\'s to database is disabled.');
      forceLocalApi = true;
    }

    if (userId <= 0) {
      logger.warn(`userId "${userId}" is invalid.`);
      forceLocalApi = true;
    }

    if (forceLocalApi && !api.isLocalApi) {
      api = makeLocalApi();
    }

    if (api.isLocalApi) {
      logger.warn('Using local API. User preferences will only be saved to the browser.');
    }

    const {data} = await api.getUserConfig(userId, 'uScope');

    forEach(data, ({id, value}, name) => {
      initPref(name, value, id);
    });

    this.isLoaded = true;
    triggerUScopeEvent(us.evUSERPREFSREADY, {status: true});
    self.events.trigger('load');
  };

  this.onLoad = (callback) => {
    if (this.isLoaded) {
      callback();
    } else {
      this.events.once('load', callback);
    }
  };

  /**
   * This function returns the user preference value for the given name. If the name is not in the pref table and is
   * not a special access pref, undefined is returned.
   * @param name
   * @returns {*}
   */
  this.get = function (name) {
    let out;
    if (userPrefTable[name]) {
      out = userPrefTable[name].value;
    } else {
      logger.error(`user-preferences::get() - requested unknown value '${name}'`);
    }

    return out;
  };

  /**
   * This function updates a user preference and stores it in the database. If the preference does not exist in the table it is added.
   * @param name
   * @param value
   */
  this.set = async function (name, value) {
    // if the name is not in the table add it
    if (!userPrefTable[name]) {
      logger.warn('Setting unknown user pref');
      initPref(name, value);
    } else if (userPrefTable[name].value == value) {
      return;
    }

    // update table
    userPrefTable[name].value = value;

    self.events.trigger(`change:${name}`, value);

    const id = (userPrefTable[name].dbId) ? userPrefTable[name].dbId : 0;

    if (id > 0) {
      api.updateConfigValue({id, userId, component: 'uScope', name, value});
    } else {
      const dbId = await api.createConfigValue({userId, component: 'uScope', name, value});
      if (dbId) {
        userPrefTable[name].id = dbId;
      }
    }
  };

  /**
   * This function initializes the user preference table
   */
  this.init = function () {
    userPrefTable = makeObservable(defaults);
    this.prefs = userPrefTable;
  };

  /**
   * This function resets the user pref's table
   * NOTE: This event's call stack appears unused. TODO: Confirm and remove.
   */
  this.reset = () => {
    Object.keys(userPrefTable).forEach(key => {
      userPrefTable[key] = clone(defaults[key]);
    });
  };

  /**
   * This function adds a set of preferences and default values to the user settings instance. Note that the same
   * preference cannot be registered twice, so it's recommended that each setting be namespaced, e.g. "report-width",
   * "snapshot-width", etc.
   * @param prefs object Simple dictionary containing keys and default values.
   */
  this.registerPreferences = (prefs) => {
    forEach(prefs, (value, key) => {
      const v = {value, dbId: null};
      defaults[key] = v;
      if (!userPrefTable.hasOwnProperty(key)) {
        userPrefTable[key] = v;
      }
    });
  };

  function initPref (name, value, dbId) {
    const o = userPrefTable[name];
    if (!o) {
      logger.warn(`InitPref: Not setting value for ${name} because it is not registered in the userpref table.`);
      return;
    }

    let typedValue;
    const valType = typeof value;
    let oType = typeof o.value;

    if (valType === oType) {
      typedValue = value;
    } else {
      // Attempt to coerce only if types are different
      oType = oType.charAt(0).toUpperCase() + oType.slice(1);
      if (oType === 'Boolean') {
        typedValue = (value === 'true');
      } else {
        typedValue = oType(value);
      }
    }

    userPrefTable[name] = {dbId, value: typedValue};
  }
}
